import { TrackedButton, Typography } from '@automata/ui';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { Theme } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import { Box, SxProps } from '@mui/system';
import { startCase } from 'lodash';
import { Fragment } from 'react';

import { selectGraphNodes } from '../../features/graph/selectors';
import {
  selectLabwareProperties,
  selectTaskPropertiesStrict,
} from '../../features/workflow/selectors';
import { useAppSelector } from '../../hooks';
import { PreviewBlocker } from '../../text';

const listItemStyle = {
  display: 'list-item',
  pl: 0.5,
  ml: 3,
  '&::marker': {
    fontWeight: 'bold',
  },
} satisfies SxProps<Theme>;

type SimulationBlockedListItemProps = {
  title: string;
  description: string;
  taskIds: string[];
  labwareIds: string[];
};

const SimulationBlockedListItem = ({
  description,
  labwareIds,
  taskIds,
  title,
}: SimulationBlockedListItemProps) => {
  const taskProperties = useAppSelector(selectTaskPropertiesStrict);
  const labwareProperties = useAppSelector(selectLabwareProperties);

  let additionalInformation = [];

  for (const taskId of taskIds) {
    const taskProperty = taskProperties[taskId];

    if (taskProperty && 'instrumentId' in taskProperty) {
      additionalInformation.push(
        `Task: ${taskProperty.nickname}, Instrument: ${startCase(
          taskProperty.instrumentId ?? ''
        )}`
      );
    }
  }
  for (const labwareId of labwareIds) {
    const labwareProperty = labwareProperties[labwareId];
    if (labwareProperty) {
      additionalInformation.push(
        `Labware: ${labwareProperty.nickname}, Slot: ${labwareProperty.slot}`
      );
    }
  }

  return (
    <ListItem sx={listItemStyle}>
      <strong>{title}</strong> {description}
      {additionalInformation.map((item, index) => (
        <Fragment key={`${item}-${index}`}>
          <br />
          <strong>{item}</strong>
        </Fragment>
      ))}
    </ListItem>
  );
};

type SimulationBlockedDisplayProps = {
  onClickReturnToWorkflow: () => void;
  previewBlockers: PreviewBlocker[];
};

const SimulationBlockedDisplay = ({
  previewBlockers,
  onClickReturnToWorkflow,
}: SimulationBlockedDisplayProps) => {
  const nodes = useAppSelector(selectGraphNodes);
  const workflowHasNodes = nodes.length > 0;

  return (
    <Stack justifyContent="center" alignItems="center" height="100%" p={3}>
      <Stack mb={3} maxWidth="35rem">
        {workflowHasNodes ? (
          <>
            <Stack direction="row">
              <Box display="flex" alignItems="center" pr={1}>
                <WarningAmber
                  fontSize="large"
                  color="error"
                  titleAccess="replan warnings present"
                />
              </Box>
              <Typography variant="h4" component="h2" mb={0}>
                Something blocks you from planning.
              </Typography>
            </Stack>
            <List sx={{ listStyleType: 'disc' }}>
              {previewBlockers.map((simulationBlocker, index) => (
                <SimulationBlockedListItem
                  key={`${simulationBlocker.title}-${index}`}
                  title={simulationBlocker.title}
                  description={simulationBlocker.description}
                  taskIds={simulationBlocker.taskIds ?? []}
                  labwareIds={simulationBlocker.labwareIds ?? []}
                />
              ))}
            </List>
          </>
        ) : (
          <>
            <Typography variant="h4" component="h2" mb={0}>
              Just a few steps away from planning!
            </Typography>

            <Typography variant="subtitle1" component="h3" mb={2}>
              To get started, please make sure everything is set up:
            </Typography>

            <List sx={{ listStyleType: 'disc' }}>
              <SimulationBlockedListItem
                title="Task-Labware connection:"
                description="Assign at least a task to a labware."
                labwareIds={[]}
                taskIds={[]}
              />
              <SimulationBlockedListItem
                title="Task duration:"
                description="Provide timing for tasks."
                labwareIds={[]}
                taskIds={[]}
              />
              <SimulationBlockedListItem
                title="Labware placement:"
                description="Define starting locations for labware."
                labwareIds={[]}
                taskIds={[]}
              />
            </List>
          </>
        )}
      </Stack>

      <TrackedButton
        trackLabel="workflowBuilder.notReadyToPreviewScreen.returnToWorkflow"
        variant="contained"
        onClick={onClickReturnToWorkflow}
      >
        Return to workflow
      </TrackedButton>
    </Stack>
  );
};

export default SimulationBlockedDisplay;
